import { type LostReasonListItem, type LostReasonListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { LostReasonFormDrawer } from '../components/LostReasonFormDrawer';
import { listLostReasons } from '../lost-reasons.api';
import {
  useBulkDeleteLostReasons,
  useBulkSetLostReasonsStatus,
  useDeleteLostReason,
  useLostReasonsList,
} from '../use-lost-reasons';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const LostReasonsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteLostReason = useDeleteLostReason();
  const bulkDeleteMut = useBulkDeleteLostReasons();
  const bulkSetStatusMut = useBulkSetLostReasonsStatus();

  const columns: ListScreenColumns<LostReasonListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<LostReasonListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<LostReasonListItem, LostReasonListQuery>
      title="Lost Reasons"
      description="Reasons a lead is marked lost (e.g. budget, booked elsewhere, no response)."
      useList={useLostReasonsList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add lost reason' }}
      renderDrawer={({ open, mode, row, close }) => (
        <LostReasonFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          lostReason={row}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} lost reason${rows.length === 1 ? '' : 's'}?`,
            description:
              'These will be soft-deleted and hidden from lists. Leads already classified with them are unaffected.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} lost reason${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will appear in pickers again.'
              : 'They will be hidden from pickers but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listLostReasons(q),
        filename: 'lost-reasons',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteLostReason.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this lost reason?',
            description: `${row.name} will be soft-deleted and hidden from lists. Leads already classified with it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No lost reasons match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load lost reasons',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
